<template>
    <div>
        <div v-if="!program.id" class="text-muted text-center py-4">
            Save the program first to configure status display names.
        </div>
        <div v-else>
            <!-- A plain paragraph rather than a b-alert: the theme paints every .alert's text
                 white and only gives the coloured variants a background, so a "light" alert
                 renders its copy white-on-white. -->
            <p class="text-muted mb-3">
                Prescribers see these names in the portal instead of the internal ones. A program
                override wins over the global default, and a status left unmapped falls back to the
                internal name, so the status is never blank. A discontinued sub-status is the
                exception: until it is mapped the portal shows nothing for it rather than the
                tenant's internal code. Some statuses also carry a workflow stage (Contacting
                Patient, Processed, Shipped) in the portal's substatus column, alongside - not
                instead of - the name set here.
            </p>

            <div class="d-flex align-items-center mb-3">
                <label class="mr-2 mb-0">Editing</label>
                <b-form-radio-group v-model="editGlobal" buttons button-variant="outline-primary" size="sm">
                    <b-form-radio :value="false">This Program</b-form-radio>
                    <b-form-radio :value="true">Global Defaults</b-form-radio>
                </b-form-radio-group>
                <HelpPopup v-if="editGlobal" class="ml-2"
                    text="Global defaults apply to every program that has no override of its own." />
            </div>

            <b-alert v-if="editGlobal" show variant="warning" class="py-2">
                You are editing the global defaults. A change here applies to every program that has
                no override of its own, not just this one.
            </b-alert>

            <b-table :items="rows" :fields="fields" :busy="loading" small striped responsive>
                <template #table-busy>
                    <div class="text-center my-3"><b-spinner small /></div>
                </template>

                <template #cell(internalName)="row">
                    {{ row.item.internalName }}
                    <b-badge v-if="!row.item.isActive" variant="secondary" class="ml-1">Inactive</b-badge>
                    <HelpPopup v-if="!row.item.isActive" class="ml-1"
                        text="This sub-status has been deactivated but still has a display name mapped, so it
                              keeps resolving on existing prescriptions. Clear the mapping to remove it." />
                    <HelpPopup v-else-if="hasWorkflowStage(row.item)" class="ml-1"
                        text="In the prescriber portal this status is shown with a workflow stage - Contacting
                              Patient, Processed or Shipped - in the substatus column. The status word itself
                              is the name set here." />
                </template>

                <template #cell(effectiveDisplayName)="row">
                    <span v-if="row.item.effectiveDisplayName">{{ row.item.effectiveDisplayName }}</span>
                    <span v-else class="text-muted font-weight-normal font-italic">nothing shown</span>
                </template>

                <template #cell(edit)="row">
                    <div class="d-flex align-items-center">
                        <b-form-input v-model="row.item.draft" size="sm" class="mr-2"
                            :placeholder="inheritedFor(row.item)" @keyup.enter="save(row.item)" />
                        <b-button size="sm" variant="primary" class="mr-1" :disabled="!isDirty(row.item)"
                            @click="save(row.item)">Save</b-button>
                        <b-button size="sm" variant="outline-secondary" :disabled="!storedValue(row.item)"
                            @click="clear(row.item)">Clear</b-button>
                    </div>
                </template>
            </b-table>
        </div>
    </div>
</template>

<script lang="ts">
import axios from 'axios';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Program } from '@/models/Program';
import {
    PrescriptionStatusDisplayEntry,
    StageAnnotatedStatusCodes,
    StatusDisplayTargetType,
    StatusDisplayTargetTypeLabels,
} from '@/models/PrescriptionStatusDisplay';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import HelpPopup from '@/components/HelpPopup.vue';

// The grid row carries its own edit box value so no side table has to be kept in step.
interface StatusDisplayRow extends PrescriptionStatusDisplayEntry {
    draft: string;
}

// Cells that show a stored value or a muted placeholder when there is none.
const optionalName = {
    formatter: (value: string | null) => value || '--',
    tdClass: (value: string | null) => (value ? '' : 'text-muted'),
};

@Component({
    name: 'ProgramStatusDisplayTab',
    components: { HelpPopup },
})
export default class ProgramStatusDisplayTab extends Vue {
    @Prop({ required: true }) program!: Program;

    private rows: StatusDisplayRow[] = [];
    private loading = false;
    private editGlobal = false;

    get fields() {
        return [
            { key: 'targetType', label: 'Type', formatter: (value: StatusDisplayTargetType) => StatusDisplayTargetTypeLabels[value] ?? '' },
            { key: 'internalName', label: 'Internal Name' },
            { key: 'globalDisplayName', label: 'Global Default', ...optionalName },
            { key: 'programDisplayName', label: 'Program Override', ...optionalName },
            { key: 'edit', label: this.editGlobal ? 'Edit Global Default' : 'Edit Program Override' },
            { key: 'effectiveDisplayName', label: 'Prescriber Sees', tdClass: 'font-weight-bold' },
        ];
    }

    // Writes go to the scope being edited; reads always go through the program so both
    // the global and program columns are populated.
    private get writeUrl(): string {
        return this.editGlobal
            ? '/api/program/status-display'
            : `/api/program/${this.program.id}/status-display`;
    }

    @Watch('program.id', { immediate: true })
    onProgramChanged() {
        if (this.program?.id) this.load();
    }

    @Watch('editGlobal')
    onScopeChanged() {
        this.resetDrafts();
    }

    /** The value the scope being edited currently stores, or '' when it stores nothing. */
    storedValue(row: StatusDisplayRow): string {
        return (this.editGlobal ? row.globalDisplayName : row.programDisplayName) ?? '';
    }

    /** What this row would show if the scope being edited stored nothing - the placeholder. */
    inheritedFor(row: StatusDisplayRow): string {
        return this.editGlobal
            ? row.internalName
            : (row.globalDisplayName || row.internalName);
    }

    isDirty(row: StatusDisplayRow): boolean {
        return row.draft.trim() !== this.storedValue(row);
    }

    /** Whether the prescriber portal shows a workflow stage beside this status. */
    hasWorkflowStage(row: StatusDisplayRow): boolean {
        return row.targetType === StatusDisplayTargetType.Status
            && StageAnnotatedStatusCodes.has(row.targetId);
    }

    // Writing a global default from inside one program's page changes what every other
    // program shows, so it is confirmed rather than taken on a single click. Program-scoped
    // writes affect only the program already on screen and go straight through.
    private async confirmGlobalWrite(row: StatusDisplayRow, verb: string): Promise<boolean> {
        if (!this.editGlobal) return true;

        const answer = await this.$bvModal.msgBoxConfirm(
            `${verb} the global default for "${row.internalName}"? This applies to every program `
            + 'that has no override of its own, not just this one.',
            {
                title: 'Change a global default',
                okTitle: verb,
                okVariant: 'warning',
                cancelTitle: 'Cancel',
            });

        return answer === true;
    }

    private resetDrafts() {
        this.rows.forEach(row => (row.draft = this.storedValue(row)));
    }

    async load() {
        this.loading = true;
        try {
            const { data } = await axios.get<PrescriptionStatusDisplayEntry[]>(
                `/api/program/${this.program.id}/status-display`);
            this.rows = (data ?? []).map(entry => ({ ...entry, draft: '' }));
            this.resetDrafts();
        } catch (e: any) {
            this.$notification(NotificationOptions.error(e));
        } finally {
            this.loading = false;
        }
    }

    async save(row: StatusDisplayRow) {
        const displayName = row.draft.trim();
        if (!displayName) {
            // Blanking the box is how an administrator reverts, and an empty stored value
            // would resolve ahead of the fallback rather than defer to it.
            await this.clear(row);
            return;
        }

        if (!await this.confirmGlobalWrite(row, 'Save')) return;

        try {
            await axios.post(this.writeUrl, {
                targetType: row.targetType,
                targetId: row.targetId,
                displayName,
            });
            this.$notification(NotificationOptions.success('Display name saved.'));
            await this.load();
        } catch (e: any) {
            this.$notification(NotificationOptions.error(e));
        }
    }

    async clear(row: StatusDisplayRow) {
        if (!this.storedValue(row)) return;

        if (!await this.confirmGlobalWrite(row, 'Clear')) return;

        try {
            await axios.delete(`${this.writeUrl}/${row.targetType}/${row.targetId}`);
            this.$notification(NotificationOptions.success('Display name cleared.'));
            await this.load();
        } catch (e: any) {
            this.$notification(NotificationOptions.error(e));
        }
    }
}
</script>
